import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { compose } from 'redux';
import { reduxForm, formValueSelector, getFormSyncErrors } from 'redux-form';
import type { InjectedFormProps, FormErrorsWithArray } from 'redux-form';

import type { RootState } from 'af-reducers';

import { useToggle } from 'af-utils/react.util';

import { TOOL_REPAIR_UPDATE } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import Loading from 'af-components/Loading';

import * as ToolRepairActions from 'af-actions/toolRepair';

import { calculateOrderItemDiffs } from './helpers';
import OrderItemDiffsModal from './OrderItemDiffsModal';
import type { DiffResult, ItemForOrder } from './types';
import Form from '../Shared/Form';
import type { OrderItemFM } from '../Shared/formModel';
import FormModel from '../Shared/formModel';
import { validate } from '../Shared/validation';

type Props = ConnectedProps<typeof connector> & InjectedFormProps<FormModel> & CustomRouteComponentProps;

const Edit = (props: Props) => {
	const { update, findById, companyName, externalRepair, initialize, errors } = props;
	const [loading, setLoading] = React.useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const { id } = useParams();

	const [pendingFormValues, setPendingFormValues] = React.useState<Nullable<FormModel>>(null);
	const [initialItems, setInitialItems] = React.useState<Nullable<OrderItemFM[]>>(null);
	const [itemDiffs, setItemDiffs] = React.useState<DiffResult<ItemForOrder>>({ added: [], changed: [], removed: [] });
	const {
		setToFalse: closeDiffModal,
		setToTrue: openDiffModal,
		value: showDiffModal,
	} = useToggle(false);

	React.useEffect(() => {
		if (!id) {
			navigate(CLIENT.COMPANY.TOOL_REPAIR.TABLE(location.state.orgAlias, companyName));
		}
		setLoading(true);
	}, [companyName, navigate, location.state.orgAlias, id]);

	React.useEffect(() => {
		if (loading) {
			const loadStuff = async () => {
				const toolRepair = await findById(parseInt(id!, 10));
				const form = new FormModel(toolRepair);
				initialize(form);
				setInitialItems(JSON.parse(JSON.stringify(form.items)));
				setLoading(false);
			};
			loadStuff();
		}
	}, [findById, id, initialize, loading]);

	const updateRepair = React.useCallback(async (form: FormModel) => {
		await update(parseInt(id!, 10), FormModel.toRequestModel(form));
		navigate(CLIENT.COMPANY.TOOL_REPAIR.TABLE(location.state.orgAlias, companyName));
	}, [update, id, navigate, location.state.orgAlias, companyName]);

	const updateRepairWithPendingValues = React.useCallback(async () => {
		if (!pendingFormValues) {
			throw new Error('Cannot save empty form');
		}
		await update(parseInt(id!, 10), FormModel.toRequestModel(pendingFormValues));
		navigate(CLIENT.COMPANY.TOOL_REPAIR.TABLE(location.state.orgAlias, companyName));
	}, [update, id, navigate, location.state.orgAlias, companyName, pendingFormValues]);

	const handleUpdateRepair = React.useCallback(async (form: FormModel) => {
		const diffs = calculateOrderItemDiffs(initialItems ?? [], form.items);

		if (diffs.added.length || diffs.changed.length || diffs.removed.length) {
			setItemDiffs(diffs);
			setPendingFormValues(form);
			openDiffModal();
			return;
		}

		updateRepair(form);
	}, [initialItems, openDiffModal, updateRepair]);

	if (loading) {
		return <Loading />;
	}

	return (
		<>
			<div className="form-segment">
				<Breadcrumbs
					items={[
						{ label: 'Tool Repairs', url: CLIENT.COMPANY.TOOL_REPAIR.TABLE(location.state.orgAlias, companyName) },
						{ label: 'Request' },
					]}
				/>
				<Form
					{...props}
					errors={errors}
					externalRepair={externalRepair}
					onSubmit={handleUpdateRepair}
				/>
			</div>
			<OrderItemDiffsModal
				diffs={itemDiffs}
				onClose={closeDiffModal}
				onSubmit={updateRepairWithPendingValues}
				showModal={showDiffModal}
			/>
		</>
	);
};

const formSelector = formValueSelector(TOOL_REPAIR_UPDATE);

interface FormActionWrapper {
	selector: <T extends keyof FormModel>(fieldName: T) => FormModel[T];
}
const formActionWrapper: Partial<FormActionWrapper> = {
	selector: undefined,
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	formActionWrapper.selector = (fieldName: keyof FormModel) => formSelector(state, fieldName);
	const errors = getFormSyncErrors(TOOL_REPAIR_UPDATE)(state) as FormErrorsWithArray<FormModel, string>;

	return {
		externalRepair: formSelector(state, 'isExternal'),
		formActionWrapper: formActionWrapper as FormActionWrapper, // selector is no longer undefined
		companyName: companyData.name,
		errors,
	};
}

function mapDispatchToProps() {
	return {
		update: ToolRepairActions.update,
		findById: ToolRepairActions.findById,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass>(
	React.memo,
	reduxForm({
		form: TOOL_REPAIR_UPDATE,
		validate,
		keepDirtyOnReinitialize: true,
		enableReinitialize: true,
	}),
	connector
);

export default enhance(Edit);
